import type { RdHoursBlockBody, RdHoursWeekdayName } from "../../../shared/api-types";

// RD Hours editing model (NO-878): the pure rules behind the Availability
// view. Times are minutes after midnight on the 30-minute step, in the RD's
// own wall clock. The break rule, bookable minutes and the visit test restate
// `@ndd/core-data`'s rd-hours module, which the client bundle must not import
// (its package root carries the Drizzle schema); `rd-hours-editor.test.ts`
// holds them to the same fixed cases. Relative imports only, so `tsx --test`
// runs it.

export const RD_HOURS_DAYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const satisfies readonly RdHoursWeekdayName[];

/** The grid runs 7 AM to 8 PM. */
export const GRID_START_MINUTE = 7 * 60;
export const GRID_END_MINUTE = 20 * 60;
export const STEP_MINUTES = 30;
export const HOUR_HEIGHT_PX = 44;
export const MINUTES_PER_DAY = 24 * 60;

export interface LunchBreak {
  start: number;
  minutes: 30 | 60;
}

export interface HoursBlock {
  start: number;
  end: number;
  lunchBreak: LunchBreak | null;
}

export type HoursDay = readonly HoursBlock[];
export type HoursWeek = Record<RdHoursWeekdayName, HoursDay>;

/** A day's blocks after an edit, and the block the edit lands on. */
export interface DayEdit {
  day: HoursDay;
  index: number;
}

const DAY_NAMES: Record<RdHoursWeekdayName, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export function dayName(day: RdHoursWeekdayName): string {
  return DAY_NAMES[day];
}

/** "Mon" */
export function dayShortName(day: RdHoursWeekdayName): string {
  return DAY_NAMES[day].slice(0, 3);
}

/** "Mo", the copy row's toggle. */
export function dayLetters(day: RdHoursWeekdayName): string {
  return DAY_NAMES[day].slice(0, 2);
}

// --- Wire shape -------------------------------------------------------------

export function clockToMinutes(value: string): number {
  const [hours, minutes] = value.split(":");

  return Number(hours) * 60 + Number(minutes);
}

export function minutesToClock(value: number): string {
  const hours = Math.floor(value / 60);
  const minutes = value % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function blocksFromBody(blocks: readonly RdHoursBlockBody[]): HoursBlock[] {
  return blocks.map((block) => ({
    start: clockToMinutes(block.start),
    end: clockToMinutes(block.end),
    lunchBreak:
      block.lunchBreak === null
        ? null
        : {
            start: clockToMinutes(block.lunchBreak.start),
            minutes: block.lunchBreak.minutes,
          },
  }));
}

export function blocksToBody(day: HoursDay): RdHoursBlockBody[] {
  return day.map((block) => ({
    start: minutesToClock(block.start),
    end: minutesToClock(block.end),
    lunchBreak:
      block.lunchBreak === null
        ? null
        : {
            start: minutesToClock(block.lunchBreak.start),
            minutes: block.lunchBreak.minutes,
          },
  }));
}

export function emptyWeek(): HoursWeek {
  return weekFrom(() => []);
}

export function weekFrom(read: (day: RdHoursWeekdayName) => HoursDay): HoursWeek {
  return Object.fromEntries(RD_HOURS_DAYS.map((day) => [day, read(day)])) as HoursWeek;
}

export function sameDay(a: HoursDay, b: HoursDay): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

// --- Editing ----------------------------------------------------------------

function snap(minute: number): number {
  return Math.round(minute / STEP_MINUTES) * STEP_MINUTES;
}

function isLunchInside(
  block: Pick<HoursBlock, "start" | "end">,
  lunch: LunchBreak
): boolean {
  return block.start <= lunch.start && lunch.start + lunch.minutes <= block.end;
}

function keepLunchIfInside(block: HoursBlock): HoursBlock {
  if (block.lunchBreak === null || isLunchInside(block, block.lunchBreak)) return block;

  return { ...block, lunchBreak: null };
}

function earlierLunch(a: LunchBreak | null, b: LunchBreak | null): LunchBreak | null {
  if (a === null) return b;

  if (b === null) return a;

  return a.start <= b.start ? a : b;
}

/**
 * Sorted, with overlapping or touching blocks joined: the API refuses touching
 * blocks, so a touch is a join. Joining two blocks that each had a break keeps
 * the earlier break.
 */
export function normalizeDay(day: HoursDay): HoursBlock[] {
  const sorted = [...day].sort((a, b) => a.start - b.start);
  const out: HoursBlock[] = [];

  for (const block of sorted) {
    const last = out[out.length - 1];

    if (last !== undefined && block.start <= last.end) {
      out[out.length - 1] = {
        start: last.start,
        end: Math.max(last.end, block.end),
        lunchBreak: earlierLunch(last.lunchBreak, block.lunchBreak),
      };
    } else {
      out.push({ ...block });
    }
  }

  return out.map(keepLunchIfInside);
}

function indexHolding(day: HoursDay, minute: number): number {
  return day.findIndex((block) => block.start <= minute && minute < block.end);
}

function withBlock(day: HoursDay, block: HoursBlock): DayEdit {
  const next = normalizeDay([...day, block]);

  return { day: next, index: indexHolding(next, block.start) };
}

/** Clicking an empty hour adds it and joins any block it touches. */
export function addHour(day: HoursDay, hourStart: number): DayEdit {
  return withBlock(day, { start: hourStart, end: hourStart + 60, lunchBreak: null });
}

/** A drag on empty space: snapped to 30 minutes, at least 30, inside the grid. */
export function createBlock(day: HoursDay, from: number, to: number): DayEdit {
  const low = Math.max(GRID_START_MINUTE, snap(Math.min(from, to)));
  const high = Math.min(GRID_END_MINUTE, snap(Math.max(from, to)));
  const end = Math.min(GRID_END_MINUTE, Math.max(high, low + STEP_MINUTES));
  const start = Math.min(low, end - STEP_MINUTES);

  return withBlock(day, { start, end, lunchBreak: null });
}

/** How far a block's start may move: the previous block's end, or the grid top. */
export function lowerBound(day: HoursDay, index: number): number {
  const previous = day[index - 1];
  const block = day[index]!;

  return previous === undefined
    ? Math.min(GRID_START_MINUTE, block.start)
    : previous.end;
}

/** How far a block's end may move: the next block's start, or the grid bottom. */
export function upperBound(day: HoursDay, index: number): number {
  const next = day[index + 1];
  const block = day[index]!;

  return next === undefined ? Math.max(GRID_END_MINUTE, block.end) : next.start;
}

/**
 * A new range for one block, or null when it breaks a bound: inside its
 * neighbours and the grid, at least 30 minutes. Reaching a neighbour joins it.
 * A lunch break that no longer fits is dropped.
 */
export function setBlockRange(
  day: HoursDay,
  index: number,
  start: number,
  end: number
): DayEdit | null {
  const block = day[index];
  if (block === undefined) return null;

  const fits = start >= lowerBound(day, index) && end <= upperBound(day, index);

  if (!fits || end - start < STEP_MINUTES) return null;

  const others = day.filter((_, position) => position !== index);

  return withBlock(others, keepLunchIfInside({ ...block, start, end }));
}

export interface RangeSteps {
  startEarlier: boolean;
  startLater: boolean;
  endEarlier: boolean;
  endLater: boolean;
}

/** Which 30-minute steps the block's Starts and Ends steppers allow. */
export function rangeSteps(day: HoursDay, index: number): RangeSteps {
  const block = day[index]!;
  const shortest = STEP_MINUTES * 2;

  return {
    startEarlier: block.start - STEP_MINUTES >= lowerBound(day, index),
    startLater: block.end - block.start >= shortest,
    endEarlier: block.end - block.start >= shortest,
    endLater: block.end + STEP_MINUTES <= upperBound(day, index),
  };
}

export function deleteBlock(day: HoursDay, index: number): HoursDay {
  return day.filter((_, position) => position !== index);
}

function replaceBlock(day: HoursDay, index: number, block: HoursBlock): HoursDay {
  return day.map((current, position) => (position === index ? block : current));
}

/** Noon when it fits, otherwise the middle of the block, 30 minutes long. */
export function addBreak(day: HoursDay, index: number): HoursDay {
  const block = day[index];
  if (block === undefined) return day;

  const noon = 12 * 60;
  const middle =
    Math.round((block.start + block.end) / 2 / STEP_MINUTES) * STEP_MINUTES;
  const start = isLunchInside(block, { start: noon, minutes: 30 })
    ? noon
    : Math.max(block.start, middle - STEP_MINUTES);

  return replaceBlock(day, index, { ...block, lunchBreak: { start, minutes: 30 } });
}

export interface BreakSteps {
  earlier: boolean;
  later: boolean;
  shorter: boolean;
  longer: boolean;
}

export function breakSteps(block: HoursBlock): BreakSteps {
  const lunch = block.lunchBreak;

  if (lunch === null)
    return { earlier: false, later: false, shorter: false, longer: false };

  const lunchEnd = lunch.start + lunch.minutes;

  return {
    earlier: lunch.start - STEP_MINUTES >= block.start,
    later: lunchEnd + STEP_MINUTES <= block.end,
    shorter: lunch.minutes === 60,
    longer: lunch.minutes === 30 && lunchEnd + STEP_MINUTES <= block.end,
  };
}

/** Moves the break by `delta` minutes, staying inside its block. */
export function moveBreak(day: HoursDay, index: number, delta: number): HoursDay {
  const block = day[index];
  const lunch = block === undefined ? null : block.lunchBreak;

  if (block === undefined || lunch === null) return day;

  const moved = { start: lunch.start + delta, minutes: lunch.minutes };

  return isLunchInside(block, moved)
    ? replaceBlock(day, index, { ...block, lunchBreak: moved })
    : day;
}

/** The break is 30 minutes or 1 hour, and stays inside its block. */
export function setBreakLength(
  day: HoursDay,
  index: number,
  minutes: 30 | 60
): HoursDay {
  const block = day[index];
  const lunch = block === undefined ? null : block.lunchBreak;

  if (block === undefined || lunch === null) return day;

  const resized = { start: lunch.start, minutes };

  return isLunchInside(block, resized)
    ? replaceBlock(day, index, { ...block, lunchBreak: resized })
    : day;
}

export function removeBreak(day: HoursDay, index: number): HoursDay {
  const block = day[index];
  if (block === undefined) return day;

  return replaceBlock(day, index, { ...block, lunchBreak: null });
}

/** The source day's blocks and breaks replace each target day's hours. */
export function copyDay(
  week: HoursWeek,
  source: RdHoursWeekdayName,
  targets: readonly RdHoursWeekdayName[]
): HoursWeek {
  const copied = week[source].map((block) => ({ ...block }));

  return weekFrom((day) =>
    targets.includes(day) && day !== source ? copied.map((b) => ({ ...b })) : week[day]
  );
}

// --- Rules (restated from @ndd/core-data rd-hours) --------------------------

interface Span {
  start: number;
  end: number;
}

/** A block's bookable spans: the block itself, split by its lunch break. */
function bookableSpans(block: HoursBlock): Span[] {
  if (block.lunchBreak === null) return [{ start: block.start, end: block.end }];

  const lunchEnd = block.lunchBreak.start + block.lunchBreak.minutes;

  return [
    { start: block.start, end: block.lunchBreak.start },
    { start: lunchEnd, end: block.end },
  ].filter((span) => span.end > span.start);
}

/** A day's continuous bookable stretches; a gap or a lunch break ends one. */
export function stretchesOf(day: HoursDay): Span[] {
  const stretches: Span[] = [];

  for (const span of day.flatMap(bookableSpans)) {
    const last = stretches[stretches.length - 1];

    if (last !== undefined && last.end === span.start) {
      last.end = span.end;
    } else {
      stretches.push({ ...span });
    }
  }

  return stretches;
}

/** The longest continuous bookable stretch; a gap or a lunch break ends one. */
export function longestStretchMinutes(day: HoursDay): number {
  return stretchesOf(day).reduce((longest, s) => Math.max(longest, s.end - s.start), 0);
}

/** The stretches strictly longer than the threshold: the ones that need a break. */
export function tooLongStretches(day: HoursDay, thresholdMinutes: number): Span[] {
  return stretchesOf(day).filter((s) => s.end - s.start > thresholdMinutes);
}

/** A stretch strictly longer than the threshold needs a break. */
export function blockNeedsBreak(block: HoursBlock, thresholdMinutes: number): boolean {
  return longestStretchMinutes([block]) > thresholdMinutes;
}

export function dayNeedsBreak(day: HoursDay, thresholdMinutes: number): boolean {
  return longestStretchMinutes(day) > thresholdMinutes;
}

/** The days, in week order, that cannot save until they have a break. */
export function breakViolations(
  week: HoursWeek,
  thresholdMinutes: number
): RdHoursWeekdayName[] {
  return RD_HOURS_DAYS.filter((day) => dayNeedsBreak(week[day], thresholdMinutes));
}

/** "Add a lunch break to Tuesday and Wednesday to save." */
export function breakRequiredMessage(days: readonly RdHoursWeekdayName[]): string {
  return `Add a lunch break to ${days.map(dayName).join(" and ")} to save.`;
}

/** "Tuesday", "Tuesday and Friday", "Monday, Tuesday and Friday". */
function dayList(days: readonly RdHoursWeekdayName[]): string {
  const names = days.map(dayName);
  const last = names.pop();

  return names.length === 0 ? (last ?? "") : `${names.join(", ")} and ${last}`;
}

/** Whether a too-long stretch on the day sits in a block that has a break. */
function breakLeavesStretch(day: HoursDay, thresholdMinutes: number): boolean {
  return tooLongStretches(day, thresholdMinutes).some((stretch) => {
    // Blocks never touch, so a stretch lies inside one block: that block's
    // own break is the one to move.
    const holder = day.filter((block) => block.start <= stretch.start).at(-1);

    return holder !== undefined && holder.lunchBreak !== null;
  });
}

/**
 * Why the week cannot save, in one sentence that names the days. When no
 * blocked day has a break yet it keeps the server's "Add a lunch break to
 * <day> to save."; once a break exists but leaves a stretch too long, it is
 * "Add or move a break on Tuesday and Friday to save." The grid and the side
 * panel name the stretch itself.
 */
export function breakBlockedMessage(week: HoursWeek, thresholdMinutes: number): string {
  const days = breakViolations(week, thresholdMinutes);

  if (days.length === 0) return "";

  const moved = days.some((day) => breakLeavesStretch(week[day], thresholdMinutes));

  return moved
    ? `Add or move a break on ${dayList(days)} to save.`
    : breakRequiredMessage(days);
}

export function dayBookableMinutes(day: HoursDay): number {
  return day
    .flatMap(bookableSpans)
    .reduce((sum, span) => sum + span.end - span.start, 0);
}

export function weekBookableMinutes(week: HoursWeek): number {
  return RD_HOURS_DAYS.reduce((sum, day) => sum + dayBookableMinutes(week[day]), 0);
}

/**
 * Whether a visit sits inside the day's hours: one block holds all of it and
 * it does not overlap that block's lunch break. A visit with no length still
 * occupies its starting minute.
 */
export function isVisitInside(
  day: HoursDay,
  startMinute: number,
  endMinute: number
): boolean {
  const end = Math.max(endMinute, startMinute + 1);
  const holder = day.find((block) => block.start <= startMinute && end <= block.end);
  const lunch = holder === undefined ? null : holder.lunchBreak;

  if (holder === undefined) return false;

  if (lunch === null) return true;

  return !(startMinute < lunch.start + lunch.minutes && lunch.start < end);
}

// --- Copy -------------------------------------------------------------------

function clockParts(minute: number): {
  hour12: number;
  minutes: number;
  period: string;
} {
  const hour = Math.floor(minute / 60) % 24;
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;

  return { hour12, minutes: minute % 60, period: hour < 12 ? "AM" : "PM" };
}

/** "9 AM", "12:30 PM": the grid's and the panel's times. */
export function timeLabel(minute: number): string {
  const { hour12, minutes, period } = clockParts(minute);
  const clock =
    minutes === 0 ? String(hour12) : `${hour12}:${String(minutes).padStart(2, "0")}`;

  return `${clock} ${period}`;
}

/** "9:30 AM", or "9:30" without the period: a booked visit's start. */
export function clockTime(minute: number, withPeriod: boolean): string {
  const { hour12, minutes, period } = clockParts(minute);
  const clock = `${hour12}:${String(minutes).padStart(2, "0")}`;

  return withPeriod ? `${clock} ${period}` : clock;
}

const FRACTIONS: Record<number, string> = { 0: "", 15: "¼", 30: "½", 45: "¾" };

/** Hours as the canvas writes them: "8", "7½", "½". */
export function hoursFigure(minutes: number): string {
  const whole = Math.floor(minutes / 60);
  const rest = Math.round((minutes % 60) / 15) * 15;
  const fraction = FRACTIONS[rest] ?? "";

  return whole === 0 && fraction !== "" ? fraction : `${whole}${fraction}`;
}

/** "9 AM to 5 PM" */
export function rangeLabel(block: Pick<HoursBlock, "start" | "end">): string {
  return `${timeLabel(block.start)} to ${timeLabel(block.end)}`;
}

// --- Keyboard ---------------------------------------------------------------

/** The empty-cell grid's roving cursor: a day column and an hour row. */
export interface GridCursor {
  day: number;
  hour: number;
}

const CURSOR_MOVES: Record<string, { day: number; hour: number }> = {
  ArrowUp: { day: 0, hour: -1 },
  ArrowDown: { day: 0, hour: 1 },
  ArrowLeft: { day: -1, hour: 0 },
  ArrowRight: { day: 1, hour: 0 },
};

const FIRST_HOUR = GRID_START_MINUTE / 60;
const LAST_HOUR = GRID_END_MINUTE / 60 - 1;

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** The cursor after an arrow key, or null for any other key. */
export function moveCursor(cursor: GridCursor, key: string): GridCursor | null {
  const move = CURSOR_MOVES[key];
  if (move === undefined) return null;

  return {
    day: clamp(cursor.day + move.day, 0, RD_HOURS_DAYS.length - 1),
    hour: clamp(cursor.hour + move.hour, FIRST_HOUR, LAST_HOUR),
  };
}

/** Enter or Space on a cell adds its hour. */
export function isAddKey(key: string): boolean {
  return key === "Enter" || key === " ";
}

export type BlockKeyAction =
  | { kind: "start"; delta: number }
  | { kind: "end"; delta: number }
  | { kind: "delete" }
  | { kind: "select" };

const ARROW_DELTAS: Record<string, number> = {
  ArrowUp: -STEP_MINUTES,
  ArrowDown: STEP_MINUTES,
};

/**
 * A focused block's keys: Shift+Arrow moves its end, Alt+Arrow its start,
 * Delete or Backspace removes it, Enter selects it.
 */
export function blockKeyAction(event: {
  key: string;
  shiftKey: boolean;
  altKey: boolean;
}): BlockKeyAction | null {
  if (event.key === "Delete" || event.key === "Backspace") return { kind: "delete" };

  if (event.key === "Enter") return { kind: "select" };

  const delta = ARROW_DELTAS[event.key];
  if (delta === undefined) return null;

  if (event.altKey) return { kind: "start", delta };

  return event.shiftKey ? { kind: "end", delta } : null;
}
